"use client";

import Link from "next/link";
import { useState } from "react";
import { PrimaryButton } from "@ui/buttons";
import { useTranslations } from "@i18n/provider";
import type { ConversationDebrief } from "../types";
import type { OverlayMessage } from "../types";
import { TranscriptAccordion } from "./TranscriptAccordion";

type DebriefViewProps = {
  debrief: ConversationDebrief;
  messages: OverlayMessage[];
  sessionId: number;
  onDone: () => void;
};

export function DebriefView({ debrief, messages, sessionId, onDone }: DebriefViewProps) {
  const { t } = useTranslations();
  const [addedWords, setAddedWords] = useState<Set<string>>(new Set());
  const [addingWord, setAddingWord] = useState<string | null>(null);

  async function handleAddWord(word: string, wordId?: number) {
    if (addedWords.has(word) || addingWord) return;
    setAddingWord(word);
    try {
      const res = await fetch("/api/agent/conversation/add-word", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ word, wordId, sessionId }),
      });
      if (res.ok) {
        setAddedWords((prev) => new Set(prev).add(word));
      }
    } finally {
      setAddingWord(null);
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto px-3 py-3">
        <TranscriptAccordion messages={messages} defaultCollapsed />

        <section className="mb-4 rounded-2xl bg-speaking-tint/50 p-3">
          <h3 className="eyebrow text-speaking-fg">{t("agent.conversation.debrief.usedWell")}</h3>
          {debrief.usedWell.length === 0 ? (
            <p className="mt-2 text-sm italic text-ink-faint">
              {t("agent.conversation.debrief.sectionEmpty")}
            </p>
          ) : (
            <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-ink-body">
              {debrief.usedWell.map((item, index) => (
                <li key={`used-${index}`}>
                  {item.text}
                  {item.example ? (
                    <span className="mt-0.5 block text-ink-muted">
                      &ldquo;{item.example}&rdquo;
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mb-4">
          <h3 className="eyebrow text-ink-faint">{t("agent.conversation.debrief.toReview")}</h3>
          {debrief.toReview.length === 0 ? (
            <p className="mt-2 text-sm italic text-ink-faint">
              {t("agent.conversation.debrief.sectionEmpty")}
            </p>
          ) : (
            <ul className="mt-2 space-y-3 text-sm text-ink-body">
              {debrief.toReview.map((item, index) => (
                <li key={`review-${index}`}>
                  <p>{item.text}</p>
                  {item.learnerText ? (
                    <p className="mt-0.5 text-ink-muted">&ldquo;{item.learnerText}&rdquo;</p>
                  ) : null}
                  {item.correction ? (
                    <p className="mt-0.5 text-ink-muted">{item.correction}</p>
                  ) : null}
                  {item.lessonHref ? (
                    <Link
                      href={item.lessonHref}
                      className="mt-1 inline-block text-sm font-semibold text-reading-fg underline-offset-2 hover:underline"
                    >
                      {t("agent.conversation.debrief.reviewLesson")}
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mb-4">
          <h3 className="eyebrow text-ink-faint">{t("agent.conversation.debrief.newWords")}</h3>
          {debrief.newWords.length === 0 ? (
            <p className="mt-2 text-sm italic text-ink-faint">
              {t("agent.conversation.debrief.sectionEmpty")}
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {debrief.newWords.map((item) => {
                const added = addedWords.has(item.word);
                return (
                  <li
                    key={item.word}
                    className="flex min-h-[44px] flex-wrap items-center justify-between gap-2 rounded-xl bg-surface-sunk px-3 py-2"
                    style={{ boxShadow: "var(--inset-field)" }}
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-ink">{item.word}</p>
                      <p className="text-sm text-ink-muted">{item.definition}</p>
                    </div>
                    <button
                      type="button"
                      disabled={added || addingWord === item.word}
                      onClick={() => void handleAddWord(item.word, item.wordId)}
                      className="min-h-[44px] rounded-full bg-speaking-tint px-3 text-sm font-semibold text-speaking-fg disabled:opacity-60"
                    >
                      {added
                        ? t("agent.conversation.debrief.addedToVocab")
                        : t("agent.conversation.debrief.addToVocab")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="mb-2">
          <h3 className="eyebrow text-ink-faint">{t("agent.conversation.debrief.fluency")}</h3>
          {debrief.fluencyNote.trim() ? (
            <p className="mt-2 text-sm italic text-ink-muted">{debrief.fluencyNote}</p>
          ) : (
            <p className="mt-2 text-sm italic text-ink-faint">
              {t("agent.conversation.debrief.sectionEmpty")}
            </p>
          )}
        </section>
      </div>

      <div
        className="flex-shrink-0 px-3 pb-3 pt-2"
        style={{ borderTop: "1px solid var(--hairline)" }}
      >
        <PrimaryButton onClick={onDone} className="min-h-[44px] w-full">
          {t("agent.conversation.debrief.done")}
        </PrimaryButton>
        <Link
          href={`/sessions/${sessionId}/postmortem`}
          className="mt-2 block w-full rounded-full border border-coral px-3 py-2 text-center text-sm font-semibold text-coral min-h-[44px] flex items-center justify-center"
        >
          Review session
        </Link>
      </div>
    </div>
  );
}
